import { useQuery } from "@tanstack/react-query";
import { api } from "../../config";
import axios from "axios";

// API instance sem authorization token para captura de leads
const apiPublic = axios.create({
    baseURL: 'https://api-n1.walasmartwork.com.br/v1',
    headers: {
        "Content-Type": "application/json",
    },
});

function useGetMarketplaceOrders(page, filter, channel, customParams = {}) {
  return useQuery({
    queryKey: ["marketplace-orders", page, filter, channel, customParams],
    queryFn: async () => {
      try {
        // Se customParams estiver presente, usar apenas eles
        if (Object.keys(customParams).length > 0) {
          const response = await api.get(`/marketplace/invoices`, {
            params: {
              page: 1,
              limit: 100,
              ...customParams
            }
          });
          return {
            orders: response.data.data || [],
            totalPages: response.data.pagination?.total_pages || 1,
            total: response.data.pagination?.total_items || 0,
            currentPage: response.data.pagination?.current_page || 1
          };
        }

        // Lógica original para compatibilidade
        // Obter a data atual formatada como YYYY-MM-DD
        const today = new Date().toISOString().split('T')[0];
        
        // Calcular a data de 4 dias atrás
        const fourDaysAgo = new Date();
        fourDaysAgo.setDate(fourDaysAgo.getDate() - 4);
        const formattedFourDaysAgo = fourDaysAgo.toISOString().split('T')[0];
        
        // Calcular ontem
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        const formattedYesterday = yesterday.toISOString().split('T')[0];

        // Configurar os parâmetros base
        const baseParams = {
          page: page + 1,
          limit: 10,
          ...(channel !== "all" && { channel })
        };

        // Adicionar filtros específicos baseados no tipo de filtro
        let filterParams = {};
        if (filter === "today") {
          // Calcular amanhã
          const tomorrow = new Date();
          tomorrow.setDate(tomorrow.getDate() + 1);
          const formattedTomorrow = tomorrow.toISOString().split('T')[0];
          
          filterParams = {
            from: today,
            to: formattedTomorrow
          };
        } else if (filter === "before") {
          filterParams = {
            from: formattedFourDaysAgo,
            to: formattedYesterday
          };
        } else if (filter === "printed") {
          filterParams = {
            printed: true
          };
        }

        const response = await api.get(`/marketplace/invoices`, {
          params: {
            ...baseParams,
            ...filterParams
          }
        });
        return {
          orders: response.data.data || [],
          totalPages: response.data.pagination?.total_pages || 1,
          total: response.data.pagination?.total_items || 0,
          currentPage: response.data.pagination?.current_page || 1
        };
      } catch (error) {
        throw new Error('Erro ao carregar pedidos: ' + error.message);
      }
    },
    keepPreviousData: true,
    staleTime: 30000 // 30 segundos
  });
}

function useGetFilters(enabled) {
    return useQuery({
        queryFn: async () => {
            const res = await api.get(`/marketplace/filters`);
            return res.data;
        },
        queryKey: ['order-filters'],
        enabled,
        retry: false,
        refetchOnWindowFocus: false
    })
}

function useCreateEcommerce(data, exec) {
    return useQuery({
        queryFn: async () => {
            const res = await api.post(`/marketplace`, data);
            return res.data;
        },
        queryKey: ['ecommerce-create'],
        enabled: exec,
        retry: false,
        refetchOnWindowFocus: false
    })
}

function useCreateEcommercePublic(data, queryParams = {}, exec) {
    return useQuery({
        queryFn: async () => {
            const res = await apiPublic.post(`/marketplace`, data, {
                params: queryParams
            });
            return res.data;
        },
        queryKey: ['ecommerce-create-public', queryParams],
        enabled: exec,
        retry: false,
        refetchOnWindowFocus: false
    })
}

function useGetEcomerce() {
    return useQuery({
        queryFn: async () => {
            const res = await api.get(`/marketplace`);
            return res.data;
        },
        queryKey: ['marketplace'],
        retry: false,
        refetchOnWindowFocus: false
    })
}

function useUpdateMarketplace(data, id, exec) {
    return useQuery({
        queryFn: async () => {
            const res = await api.put(`/marketplace/${id}`, data);
            return res.data;
        },
        queryKey: ['marketplace-update'],
        enabled: exec,
        retry: false,
        refetchOnWindowFocus: false
    })
}

function useUploadSkusImportados(file, exec) {
    return useQuery({
      queryFn: async () => {
        const formData = new FormData();
        formData.append("file",  file);
  
        const res = await api.post(`/marketplace/skus/importados`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
  
        return res.data;
      },
      queryKey: ["upload-skus-importados"],
      enabled: exec && !!file,
      retry: false,
      refetchOnWindowFocus: false,
    });
}
  
function useUploadSkusNormais(file, exec) {
    return useQuery({
      queryFn: async () => {
        const formData = new FormData();
        formData.append("file", file);
  
        const res = await api.post(`/marketplace/skus/normals`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
  
        return res.data;
      },
      queryKey: ["upload-skus-normais"],
      enabled: exec && !!file,
      retry: false,
      refetchOnWindowFocus: false,
    });
}

function useGetPurchaseOrders(params, enabl) {
    return useQuery({
        queryFn: async () => {
            const res = await api.get(`/purchase`, {params});
            return res.data;
        },
        queryKey: ['purchase-orders'],
        enabled: enabl,
        retry: false,
        refetchOnWindowFocus: false
    })
}

function useGetAvailableIntegrations(params = {}, enabled = true) {
  return useQuery({
    queryFn: async () => {
      const res = await api.get(`/marketplace/integrations`, { params });
      return res.data;
    },
    queryKey: ['available-integrations', params],
    enabled,
    retry: false,
    refetchOnWindowFocus: false
  });
}

function useGetRequestMercadoLivreOAuthURI(channel, enabled = true) {
  return useQuery({
    queryFn: async () => {
      const res = await api.get(`/oauth/request`, { 
        params: { channel }
      });
      return res.data;
    },
    queryKey: ['oauth-request', channel],
    enabled: enabled && !!channel,
    retry: false,
    refetchOnWindowFocus: false,
  });
}

function useGetMarketplaceChannels(params = {}, enabled = true) {
  return useQuery({
    queryFn: async () => {
      const res = await api.get(`/marketplace/channels`, { params });
      return res.data;
    },
    queryKey: ['marketplace-channels', params],
    enabled,
    retry: false,
    refetchOnWindowFocus: false,
    keepPreviousData: true
  });
}

function useUpdateMarketplaceChannelStatus(id, data, exec = false) {
  return useQuery({
    queryFn: async () => {
      const res = await api.put(`/marketplace/channels/${id}`, data);
      return res.data;
    },
    queryKey: ['marketplace-channel-update', id],
    enabled: exec,
    retry: false,
    refetchOnWindowFocus: false
  });
}

function useDeleteMarketplaceChannel(id, exec = false) {
  return useQuery({
    queryFn: async () => {
      if (!id) {
        throw new Error("ID da integração não fornecido");
      }
      const res = await api.delete(`/marketplace/channels/${id}`);
      return res.data;
    },
    queryKey: ['marketplace-channel-delete', id],
    enabled: exec && !!id,
    retry: false,
    refetchOnWindowFocus: false
  });
}

function useCreateMarketplaceChannel(data, exec = false) {
  return useQuery({
    queryFn: async () => {
      const res = await api.post(`/marketplace/channels`, data);
      return res.data;
    },
    queryKey: ['marketplace-channel-create'],
    enabled: exec,
    retry: false,
    refetchOnWindowFocus: false
  });
}

function useGetMarketplaceActualTaxProfile(enabled = true) {
  return useQuery({
    queryFn: async () => {
      const res = await api.get(`/marketplace/taxes/marketplace`);
      return res.data;
    },
    queryKey: ['marketplace-tax-profile'],
    enabled,
    retry: false,
    refetchOnWindowFocus: false,
    keepPreviousData: true
  });
}

function useGetRequestBlingOAuthURI(channel, enabled = true) {
  return useQuery({
    queryFn: async () => {
      const res = await api.get(`/oauth/request`, {
        params: { channel }
      });
      return res.data;
    },
    queryKey: ['oauth-request', channel],
    enabled: enabled && !!channel,
    retry: false,
    refetchOnWindowFocus: false,
  });
}

function useGetRequestShopeeOAuthURI(channel, enabled = true) {
  return useQuery({
    queryFn: async () => {
      const res = await api.get(`/oauth/request`, {
        params: { channel }
      });
      return res.data;
    },
    queryKey: ['oauth-request', channel],
    enabled: enabled && !!channel,
    retry: false,
    refetchOnWindowFocus: false,
  });
}

function useUpdateMarketplaceActualTaxProfileConfiguration(id, body, enabled = false) {
  return useQuery({
    queryFn: async () => {
      const res = await api.put(`/marketplace/taxes/${id}`, body);
      return res.data;
    },
    queryKey: ['marketplace-tax-profile-update-configuration', id],
    enabled,
    retry: false,
    refetchOnWindowFocus: false,
    keepPreviousData: true
  });
}

function useUpdateMarketplaceActualTaxProfile(body, enabled = false) {
  return useQuery({
    queryFn: async () => {
      const res = await api.put(`/marketplace/taxes/marketplace/profile`, body);
      return res.data;
    },
    queryKey: ['marketplace-tax-profile-update'],
    enabled,
    retry: false,
    refetchOnWindowFocus: false,
    keepPreviousData: true
  });
}

function useGetMarketplaceInvoice(fiscalKey, enabled = true) {
  return useQuery({
    queryFn: async () => {
      const res = await api.get(`/marketplace/invoices/${fiscalKey}`);
      return res.data;
    },
    queryKey: ['marketplace-invoice', fiscalKey],
    enabled: enabled && !!fiscalKey,
    retry: false,
    refetchOnWindowFocus: false
  });
}

function useGetMarketplaceInvoiceLabel(fiscalKey, responseType = 'pdf', enabled = true) {
  return useQuery({
    queryFn: async () => {
      const config = {
        params: { response_type: responseType }
      };

      // Only set responseType to blob for PDF, let ZPL come as text
      if (responseType === 'pdf') {
        config.responseType = 'blob';
      }

      const res = await api.get(`/marketplace/invoices/${fiscalKey}/label`, config);
      return res.data;
    },
    queryKey: ['marketplace-invoice-label', fiscalKey, responseType],
    enabled: enabled && !!fiscalKey,
    retry: false,
    refetchOnWindowFocus: false
  });
}

function useGetMarketplacePlans(enabled = true) {
  return useQuery({
    queryFn: async () => {
      const res = await api.get(`/marketplace/plans`);
      return res.data;
    },
    queryKey: ['marketplace-plans'],
    enabled,
    retry: false,
    refetchOnWindowFocus: false
  });
}

function useMarkInvoiceAsPrinted(fiscalKeyOrId, enabled = false) {
  return useQuery({
    queryFn: async () => {
      const res = await api.patch(`/marketplace/invoices/${fiscalKeyOrId}/printed`, {
        printed: true
      });
      return res.data;
    },
    queryKey: ['marketplace-invoice-mark-printed', fiscalKeyOrId],
    enabled: enabled && !!fiscalKeyOrId,
    retry: false,
    refetchOnWindowFocus: false
  });
}

function useGetMarketplaceSkusQuantities(params = {}, enabled = true) {
  return useQuery({
    queryFn: async () => {
      const res = await api.get(`/marketplace/invoices/skus/quantities`, { 
        params: {
          from: params.from,
          to: params.to,
          limit: params.limit || 20,
          page: params.page || 1
        }
      });
      return res.data;
    },
    queryKey: ['marketplace-skus-quantities', params],
    enabled: Boolean(enabled && params.from && params.to),
    retry: false,
    refetchOnWindowFocus: false,
    keepPreviousData: true,
    staleTime: 30000 // 30 segundos
  });
}

function useGetMarketplaceSkusMetrics(params = {}, enabled = true) {
  return useQuery({
    queryFn: async () => {
      const res = await api.get(`/marketplace/invoices/skus/metrics`, { 
        params: {
          from: params.from,
          to: params.to
        }
      });
      return res.data;
    },
    queryKey: ['marketplace-skus-metrics', params],
    enabled: Boolean(enabled && params.from && params.to),
    retry: false,
    refetchOnWindowFocus: false,
    keepPreviousData: true,
    staleTime: 30000 // 30 segundos
  });
}

export {
    useGetMarketplaceOrders,
    useGetFilters,
    useCreateEcommerce,
    useCreateEcommercePublic,
    useGetEcomerce,
    useUpdateMarketplace,
    useUploadSkusImportados,
    useUploadSkusNormais,
    useGetPurchaseOrders,
    useGetAvailableIntegrations,
    useGetMarketplaceChannels,
    useUpdateMarketplaceChannelStatus,
    useDeleteMarketplaceChannel,
    useCreateMarketplaceChannel,
    useGetRequestMercadoLivreOAuthURI,
    useGetRequestBlingOAuthURI,
    useGetRequestShopeeOAuthURI,
    useGetMarketplaceActualTaxProfile,
    useUpdateMarketplaceActualTaxProfileConfiguration,
    useUpdateMarketplaceActualTaxProfile,
    useGetMarketplaceInvoice,
    useGetMarketplaceInvoiceLabel,
    useGetMarketplacePlans,
    useMarkInvoiceAsPrinted,
    useGetMarketplaceSkusQuantities,
    useGetMarketplaceSkusMetrics
}